"use client";

import React from "react";

import Image from "next/image";
import {cn} from "@/lib/utils";
import {PortableText} from "@portabletext/react";
import {richTextComponent} from "@/sanity/sanityPortableText";

import Chip from "@/components/global/ui/Chip";
import CornerList from "@/components/global/ui/CornerList";
import DecorationLine from "@/components/global/ui/DecorationLine";
import {CTACornerButton} from "@/shadcn/ui/corner-button";

import { PageVslLongSchema } from "@/sanity/pages/PageVslLongTemplate";
import dots from "@/public/vsl/dotsSvg.svg"

export default function ComponentsSection({
  data,
  className,
}: {
  data: PageVslLongSchema;
  className?: string;
}) {
  return (
    <section
      className={cn(
        "relative overflow-clip bg-[#0A1A1D]",
        className,
      )}
    >
      {/* side lines flanking the content */}
      <DecorationLine
      className="top-0"
        orientation="vertical"
        position="left"
        style="light"
      />
      <DecorationLine
      className="top-0"
        orientation="vertical"
        position="right"
        style="light"
      />

      <div className="relative w-[min(164rem,100%)] mx-auto px-[2rem] sm:px-[3rem] lg:px-[7rem] pt-[9rem] lg:pt-[14rem] pb-[9rem] lg:pb-[20rem]">
        <Image
          src={dots}
          alt=""
          aria-hidden="true"
          className="pointer-events-none absolute left-1/2 top-0 -translate-x-1/2 w-[min(120rem,90%)] select-none"
        />

        {/* header */}
        <div className="relative z-10 flex flex-col items-center text-center pb-[9rem] lg:pb-[13rem]">
          {data?.componentsChip?.text && (
            <Chip
              className="[&_p]:uppercase"
              image={data?.componentsChip?.icon}
              text={data?.componentsChip?.text}
            />
          )}

          {data?.componentsTitle && (
            <div className="richtext richtext-center w-[min(90rem,100%)] prose-headings:font-funnel prose-headings:font-medium prose-headings:text-white prose-headings:text-[4rem] sm:prose-headings:text-[5rem] lg:prose-headings:text-[8rem] prose-headings:leading-[1.15] prose-headings:text-balance prose-em:font-ivy">
              <PortableText
                value={data.componentsTitle}
                components={richTextComponent}
              />
            </div>
          )}

          {data?.componentsText && (
            <div className="richtext richtext-center mt-[1.5rem] w-[min(63rem,100%)] prose-p:text-white/60 prose-p:text-[2rem] lg:prose-p:text-[2.25rem] prose-p:leading-[1.5]">
              <PortableText
                value={data.componentsText}
                components={richTextComponent}
              />
            </div>
          )}

          {data?.componentsCTA && (
            <CTACornerButton
              className="mt-[3rem]"
              data={data.componentsCTA}
            />
          )}
        </div>

        {/* cards grid */}
        {data?.componentsItems && data.componentsItems.length > 0 && (
          <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-[3rem]">
            {data.componentsItems.map((item, index) => (
              <div
                key={index}
                className="flex flex-col gap-[3rem] bg-[#112628] border-t-[3px] border-t-[#D9F9B9] p-[3rem] lg:p-[4rem]"
              >
                {item?.icon?.src && (
                  <div className="flex items-center justify-center w-[6rem] h-[6rem] rounded-[.8rem] bg-[#D9F9B9]/[0.08]">
                    <Image
                      className="!w-[4rem] !h-[4rem] object-contain"
                      src={item.icon}
                      alt=""
                    />
                  </div>
                )}

                {item?.title && (
                  <h3 className="font-geist font-medium text-white text-[2.4rem] lg:text-[2.8rem] leading-[1.2]">
                    {item.title}
                  </h3>
                )}

                {item?.list && item.list.length > 0 && (
                  <CornerList items={item.list} color="#D9F9B9" />
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
